import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { UploadedReceiptStatus } from '../../../types';

interface ReceiptStatusBadgeProps {
  status: UploadedReceiptStatus;
  count?: number;
  showAll?: boolean;
  labelOverride?: string;
}

const ReceiptStatusBadge: React.FC<ReceiptStatusBadgeProps> = ({
  status,
  count,
  showAll = false,
  labelOverride,
}) => {
  // Get status styling
  const getStatusStyle = (status: UploadedReceiptStatus) => {
    switch (status) {
      case 'pending':
        return {
          icon: (
            <div className='w-3 h-3 rounded-full bg-gray-400 dark:bg-gray-500' />
          ),
          text: 'text-gray-700 dark:text-gray-300',
          background: 'bg-gray-100 dark:bg-gray-800',
          border: 'border-gray-300 dark:border-gray-600',
          label: 'Waiting for text extraction',
        };
      case 'text-extracting':
        return {
          icon: (
            <div className='w-3 h-3 rounded-full bg-yellow-500 dark:bg-yellow-400 animate-pulse' />
          ),
          text: 'text-yellow-700 dark:text-yellow-300',
          background: 'bg-yellow-100 dark:bg-yellow-900/20',
          border: 'border-yellow-300 dark:border-yellow-600',
          label: 'Text extracting',
        };
      case 'ready':
        return {
          icon: (
            <div className='w-3 h-3 rounded-full bg-blue-500 dark:bg-blue-400' />
          ),
          text: 'text-blue-700 dark:text-blue-300',
          background: 'bg-blue-100 dark:bg-blue-900/20',
          border: 'border-blue-300 dark:border-blue-600',
          label: 'Ready for booking generation',
        };
      case 'generated':
        return {
          icon: (
            <CheckCircleIcon className='w-4 h-4 text-blue-600 dark:text-blue-400' />
          ),
          text: 'text-blue-700 dark:text-blue-300',
          background: 'bg-blue-100 dark:bg-blue-900/20',
          border: 'border-blue-300 dark:border-blue-600',
          label: 'Ready to import',
        };
      case 'imported':
        return {
          icon: (
            <CheckCircleIcon className='w-4 h-4 text-green-600 dark:text-green-400' />
          ),
          text: 'text-green-700 dark:text-green-300',
          background: 'bg-green-100 dark:bg-green-900/20',
          border: 'border-green-300 dark:border-green-600',
          label: 'Imported',
        };
      case 'modified':
        return {
          icon: (
            <div className='w-3 h-3 rounded-full bg-blue-500 dark:bg-blue-400' />
          ),
          text: 'text-blue-700 dark:text-blue-300',
          background: 'bg-blue-100 dark:bg-blue-900/20',
          border: 'border-blue-300 dark:border-blue-600',
          label: 'Booking modified',
        };
      case 'text-extraction-failed':
        return {
          icon: (
            <XCircleIcon className='w-4 h-4 text-red-600 dark:text-red-400' />
          ),
          text: 'text-red-700 dark:text-red-300',
          background: 'bg-red-100 dark:bg-red-900/20',
          border: 'border-red-300 dark:border-red-600',
          label: 'Text extraction failed',
        };
      case 'generation-scheduled':
        return {
          icon: (
            <div className='w-3 h-3 rounded-full bg-yellow-500 dark:bg-yellow-400 animate-pulse' />
          ),
          text: 'text-yellow-700 dark:text-yellow-300',
          background: 'bg-yellow-100 dark:bg-yellow-900/20',
          border: 'border-yellow-300 dark:border-yellow-600',
          label: 'Generation scheduled',
        };
      case 'generating':
        return {
          icon: (
            <div className='w-3 h-3 rounded-full bg-yellow-500 dark:bg-yellow-400 animate-pulse' />
          ),
          text: 'text-yellow-700 dark:text-yellow-300',
          background: 'bg-yellow-100 dark:bg-yellow-900/20',
          border: 'border-yellow-300 dark:border-yellow-600',
          label: 'Booking generating',
        };
      case 'generation-failed':
        return {
          icon: (
            <XCircleIcon className='w-4 h-4 text-red-600 dark:text-red-400' />
          ),
          text: 'text-red-700 dark:text-red-300',
          background: 'bg-red-100 dark:bg-red-900/20',
          border: 'border-red-300 dark:border-red-600',
          label: 'Booking generation failed',
        };
      case 'import-scheduled':
        return {
          icon: (
            <div className='w-3 h-3 rounded-full bg-yellow-500 dark:bg-yellow-400 animate-pulse' />
          ),
          text: 'text-yellow-700 dark:text-yellow-300',
          background: 'bg-yellow-100 dark:bg-yellow-900/20',
          border: 'border-yellow-300 dark:border-yellow-600',
          label: 'Import scheduled',
        };
      case 'importing':
        return {
          icon: (
            <div className='w-3 h-3 rounded-full bg-yellow-500 dark:bg-yellow-400 animate-pulse' />
          ),
          text: 'text-yellow-700 dark:text-yellow-300',
          background: 'bg-yellow-100 dark:bg-yellow-900/20',
          border: 'border-yellow-300 dark:border-yellow-600',
          label: 'Booking importing',
        };
      case 'import-failed':
        return {
          icon: (
            <XCircleIcon className='w-4 h-4 text-red-600 dark:text-red-400' />
          ),
          text: 'text-red-700 dark:text-red-300',
          background: 'bg-red-100 dark:bg-red-900/20',
          border: 'border-red-300 dark:border-red-600',
          label: 'Booking import failed',
        };
      default:
        return {
          icon: (
            <div className='w-3 h-3 rounded-full bg-gray-400 dark:bg-gray-500' />
          ),
          text: 'text-gray-700 dark:text-gray-300',
          background: 'bg-gray-100 dark:bg-gray-800',
          border: 'border-gray-300 dark:border-gray-600',
          label: status,
        };
    }
  };

  const statusStyle = getStatusStyle(status);

  // Build display text
  const label = labelOverride || statusStyle.label;
  const displayText = showAll
    ? `All ${label}`
    : count
      ? `${count} ${label}`
      : label;

  return (
    <div
      className={`flex items-center flex-shrink-0 text-sm px-3 py-1 rounded-full border ${statusStyle.background} ${statusStyle.border}`}
    >
      {statusStyle.icon}
      <span className={`ml-2 ${statusStyle.text}`}>{displayText}</span>
    </div>
  );
};

export default ReceiptStatusBadge;
